import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  BarChart3,
  Bot,
  LayoutDashboard,
  LogOut,
  Rocket,
  Settings,
  Trophy,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/auth/AuthProvider";

type NavItem = {
  href: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
};

const NAV_ITEMS: NavItem[] = [
  { href: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { href: "/leaderboard", label: "Leaderboard", icon: Trophy },
  { href: "/gtm-engine", label: "GTM Engine", icon: Rocket },
  { href: "/autopilot", label: "Autopilot", icon: Bot },
  { href: "/analytics", label: "Analytics", icon: BarChart3 },
];

export function Sidebar() {
  const { pathname } = useLocation();
  const { user, signOut } = useAuth();
  const [avatarError, setAvatarError] = useState(false);

  return (
    <aside className="flex h-screen w-60 flex-col border-r bg-slate-50/60">
      <div className="border-b px-5 py-4">
        <Link to="/dashboard" className="flex items-center gap-2">
          <img
            src="/logo.png"
            alt="CloudStudio"
            className="h-8 w-8 rounded-lg"
          />
          <div className="flex flex-col">
            <span className="text-sm font-semibold leading-tight">
              CloudStudio
            </span>
            <span className="text-xs leading-tight text-muted-foreground">
              Command Center
            </span>
          </div>
        </Link>
      </div>

      <nav className="flex-1 space-y-1 p-3">
        {NAV_ITEMS.map((item) => {
          const Icon = item.icon;
          const active =
            pathname === item.href || pathname.startsWith(`${item.href}/`);
          return (
            <Link
              key={item.href}
              to={item.href}
              className={cn(
                "flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors",
                active
                  ? "bg-primary text-primary-foreground"
                  : "text-muted-foreground hover:bg-slate-200/60 hover:text-foreground"
              )}
            >
              <Icon className="h-4 w-4" />
              {item.label}
            </Link>
          );
        })}
      </nav>

      <div className="border-t p-3">
        <Link
          to="/settings"
          className={cn(
            "flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors",
            pathname.startsWith("/settings")
              ? "bg-primary text-primary-foreground"
              : "text-muted-foreground hover:bg-slate-200/60 hover:text-foreground"
          )}
        >
          <Settings className="h-4 w-4" />
          Settings
        </Link>

        <div className="mt-3 flex items-center gap-3 rounded-md px-3 py-2">
          {user?.image && !avatarError ? (
            <img
              src="/api/auth/avatar"
              alt={user.name ?? user.email ?? "User"}
              className="h-8 w-8 rounded-full"
              onError={() => setAvatarError(true)}
            />
          ) : (
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-slate-200 text-xs font-medium">
              {(user?.name || user?.email || "?").charAt(0).toUpperCase()}
            </div>
          )}
          <div className="min-w-0 flex-1">
            <div className="truncate text-xs font-medium">
              {user?.name ?? "User"}
            </div>
            <div className="truncate text-[10px] text-muted-foreground">
              {user?.email}
            </div>
          </div>
          <button
            type="button"
            onClick={signOut}
            className="rounded p-1.5 text-muted-foreground hover:bg-slate-200/60 hover:text-foreground"
            title="Sign out"
          >
            <LogOut className="h-4 w-4" />
          </button>
        </div>
      </div>
    </aside>
  );
}
